import { DownloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Button, Empty, Modal, message, Typography, theme, Grid } from 'antd';
import BitacoraEdificio from 'components/BitacoraEdificio';
import MaintanceToDoContainer from 'components/MaintanceToDoContainer';
import AscensoresMaintanceToDoForm from 'components/forms/AscensoresMaintanceToDoForm';
import FinishMaintanceForm from 'components/forms/FinishMaintanceForm';
import GeneralMaintanceToDoForm from 'components/forms/GeneralMaintanceToDoForm';
import { improveTexts } from 'api/aiText';
import RevisarRedaccionDrawer from 'components/mantenciones/RevisarRedaccionDrawer';
import MantencionHeader from 'components/mantenciones/MantencionHeader';
import ComponentesEquipo from 'components/admin/Equipos/ComponentesEquipo';
import MantencionBottomNav from 'components/mantenciones/MantencionBottomNav';
import MantencionTopTabs from 'components/mantenciones/MantencionTopTabs';
import MantencionTabShell from 'components/mantenciones/MantencionTabShell';
import { useMaintanceToDo } from 'providers/MaintanceToDoProvider';
import { useObservaciones } from 'providers/ObservacionesProvider';
import { useOneMantencion } from 'providers/OneMantencionProvider';
import { useSolicitudesMantencion } from 'providers/SolicitudesMantencionProvider';
import { useVisita } from 'hooks/useVisita';
import useMantencionConfig from 'hooks/useMantencionConfig';
import dayjs from 'dayjs';
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

const defaultModalProps = {
  children: null,
  title: 'Error',
  open: false,
};

function ChecklistTab({ Ascensores, activeAscensorId, setActiveAscensorId, ascensorStats }) {
  return (
    <MaintanceToDoContainer
      ascensores={Ascensores}
      activeAscensorId={activeAscensorId}
      setActiveAscensorId={setActiveAscensorId}
      ascensorStats={ascensorStats}
    />
  );
}

// ---------------------------------------------------------------------------
// Mantencion — página principal de ejecución de mantención.
// ---------------------------------------------------------------------------
const Mantencion = () => {
  const { id } = useParams();
  const location = useLocation();

  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isDesktop = screens.lg;
  const { call, isLoading, data, hasCalled } = useOneMantencion();
  const { iniciarVisita } = useVisita();
  const mantCfg = useMantencionConfig();
  const {
    save,
    saveStatus,
    setFlatActividades,
    validarYNotificarEstados,
    validarYNotificarSeveridades,
    obsSug,
    observaciones: observacionesProvider,
    aplicarTextosCorregidos,
    estados,
    bindMantencion,
    isOnline,
    hasUnsyncedChanges,
    registerAscensorNavigator,
  } = useMaintanceToDo();
  const { openDowloadMantencion } = useSolicitudesMantencion();

  useEffect(() => {
    if (!hasCalled) {
      call(id);
    }
  }, [call, hasCalled, id]);

  useEffect(() => {
    if (id) bindMantencion(id);
  }, [id, bindMantencion]);

  const { mantencion, Ascensores } = useMemo(() => {
    return data || {};
  }, [data]);

  // Alimenta al provider con la lista plana de TODAS las actividades de todos
  // los ascensores (los containers están en tabs y cada uno solo ve las suyas;
  // si seteamos desde ahí, la validación de severidades al finalizar solo
  // encontraría las del último tab renderizado).
  useEffect(() => {
    if (!Ascensores) return;
    const flat = Ascensores.flatMap((a) => a?.ActividadRespuestas || []);
    setFlatActividades(flat);
  }, [Ascensores, setFlatActividades]);

  const [activeAscensorId, setActiveAscensorId] = useState(null);
  useEffect(() => {
    if (!activeAscensorId && Ascensores?.length) {
      setActiveAscensorId(Ascensores[0].id);
    }
  }, [Ascensores, activeAscensorId]);

  // El provider usa este setter para saltar a la pestaña del ascensor que tiene
  // el ítem pendiente cuando el técnico pulsa "ir a corregir" en el gate de cierre.
  useEffect(() => {
    registerAscensorNavigator(setActiveAscensorId);
  }, [registerAscensorNavigator]);

  // Resumen por ascensor: cuántas actividades pendientes (sin estado) y
  // cuántas con problema incompleto (RECHAZADO sin obs o severidad). Se
  // usa en los chips del selector para que el técnico vea de un vistazo
  // dónde le queda trabajo sin abrir cada ascensor.
  const exigeSeveridad = mantCfg.severidad && mantCfg.severidadObligatoria;
  const ascensorStats = useMemo(() => {
    const out = {};
    (Ascensores || []).forEach((asc) => {
      const acts = asc?.ActividadRespuestas || [];
      let pendientes = 0;
      let problemasIncompletos = 0;
      let conProblema = 0;
      acts.forEach((a) => {
        if (!a.estado) pendientes += 1;
        if (a.estado === 'RECHAZADO') {
          conProblema += 1;
          const obs = a.observacion && String(a.observacion).trim() !== '';
          if (!obs || (exigeSeveridad && !a.severidad)) problemasIncompletos += 1;
        }
      });
      out[asc.id] = {
        total: acts.length,
        pendientes,
        conProblema,
        problemasIncompletos,
        completo: pendientes === 0 && problemasIncompletos === 0,
      };
    });
    return out;
  }, [Ascensores, exigeSeveridad]);

  const [iniciando, setIniciando] = useState(false);
  const onIniciarVisita = async () => {
    setIniciando(true);
    try {
      await iniciarVisita({ tipo: 'maintenance', id: Number(id) });
      message.success('Visita iniciada en sitio');
      // Recargar la mantención para reflejar inicio_visita_at actualizado
      await call(id);
    } catch (e) {
      message.error('No se pudo iniciar la visita');
    } finally {
      setIniciando(false);
    }
  };

  const [finishOpen, setFinishOpen] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const [revisionItems, setRevisionItems] = useState([]);

  // Junta los textos que van al informe del cliente, con contexto acotado.
  // Multi-hallazgo: una entrada por hallazgo, key "general" | "<idRes>:<tipo>:<tempId>".
  const juntarTextosInforme = () => {
    const flat = (Ascensores || []).flatMap((a) => a?.ActividadRespuestas || []);
    const items = [];
    flat.forEach((a) => {
      const idRes = a.id;
      const item = obsSug?.[idRes];
      if (!item) return;
      const estado = a.estado ?? '';
      const titulo = a?.Actividad?.actividad ?? a?.actividad?.actividad ?? '';
      (item.incidencias || []).forEach((h) => {
        if (h.descripcion && String(h.descripcion).trim() !== '') {
          items.push({
            key: `${idRes}:INCIDENCIA:${h.tempId}`,
            label: `Incidencia — ${titulo || `Ítem ${idRes}`}`,
            text: String(h.descripcion),
            context: `Ítem: ${titulo} — Estado: ${estado} — Tipo: incidencia`,
          });
        }
      });
      (item.sugerencias || []).forEach((h) => {
        if (h.descripcion && String(h.descripcion).trim() !== '') {
          items.push({
            key: `${idRes}:SUGERENCIA:${h.tempId}`,
            label: `Sugerencia — ${titulo || `Ítem ${idRes}`}`,
            text: String(h.descripcion),
            context: `Ítem: ${titulo} — Estado: ${estado} — Tipo: sugerencia`,
          });
        }
      });
    });
    const general = observacionesProvider ?? mantencion?.observaciones;
    if (general && String(general).trim() !== '') {
      items.push({
        key: 'general',
        label: 'Observación general',
        text: String(general),
        context: 'Observación general del informe de mantención',
      });
    }
    return items;
  };

  const openFinish = async () => {
    // Validación ANTES del save: obsSug tiene las ediciones frescas de esta
    // sesión; save() las persiste y luego limpia obsSug, así que si validamos
    // después la validación no tiene cómo ver los cambios recientes.
    // Primero exigir que todo ítem visible tenga estado, luego las severidades.
    if (!validarYNotificarEstados()) return;
    if (!validarYNotificarSeveridades()) return;
    if (!(await save(id))) return; // save() ya avisó del error; no seguir a IA/firmas si no guardó

    if (!mantCfg.aiRedaccion) {
      setFinishOpen(true);
      return;
    }

    // Revisión IA best-effort: si falla o no hay textos, seguimos a firmas.
    const textos = juntarTextosInforme();
    if (textos.length === 0) {
      setFinishOpen(true);
      return;
    }
    setRevisionLoading(true);
    setRevisionOpen(true);
    try {
      const { items } = await improveTexts(textos);
      const byKey = new Map(items.map((i) => [i.key, i]));
      const merged = textos.map((t) => {
        const r = byKey.get(t.key);
        return {
          key: t.key,
          label: t.label,
          context: t.context,
          original: t.text,
          improved: r?.improved ?? t.text,
          changed: Boolean(r?.changed),
        };
      });
      const conCambios = merged.filter((m) => m.changed);
      if (conCambios.length === 0) {
        setRevisionOpen(false);
        setFinishOpen(true);
        return;
      }
      setRevisionItems(merged);
    } catch (e) {
      // IA nunca bloquea el cierre.
      setRevisionOpen(false);
      setFinishOpen(true);
    } finally {
      setRevisionLoading(false);
    }
  };

  // Confirma las correcciones elegidas: aplica al state, re-guarda y abre firmas.
  const handleRevisionConfirm = async (finales) => {
    aplicarTextosCorregidos(finales);
    setRevisionOpen(false);
    if (!(await save(id))) return; // si no guardó, no avanzar a firmas
    setFinishOpen(true);
  };

  // Omitir correcciones: continuar directo a firmas.
  const handleRevisionSkip = () => {
    setRevisionOpen(false);
    setFinishOpen(true);
  };

  // Regenera la sugerencia de UN ítem desde su texto original + una instrucción
  // libre del técnico (ej. "más formal"). Best-effort: ante fallo mantiene la
  // sugerencia previa y avisa. Devuelve true si aplicó una nueva sugerencia.
  const handleRegenerate = async (key, instruction) => {
    const item = revisionItems.find((i) => i.key === key);
    if (!item) return false;
    try {
      const { items } = await improveTexts([
        { key, text: item.original, context: item.context, instruction },
      ]);
      const r = items.find((i) => i.key === key);
      if (!r) {
        message.warning('No se pudo regenerar la sugerencia. Se mantiene la anterior.');
        return false;
      }
      setRevisionItems((prev) =>
        prev.map((i) =>
          i.key === key ? { ...i, improved: r.improved ?? i.improved, changed: true } : i
        )
      );
      return true;
    } catch (e) {
      message.warning('No se pudo regenerar la sugerencia. Se mantiene la anterior.');
      return false;
    }
  };

  const [modalProps, setModalProps] = useState(defaultModalProps);

  const { callForClient: callObservaciones, dataForClient: observaciones } = useObservaciones();

  useEffect(() => {
    if (mantencion?.id_cliente) {
      callObservaciones(mantencion?.id_cliente);
    }
  }, [callObservaciones, mantencion?.id_cliente]);

  const params = new URLSearchParams(location.search);
  const onlyEdit = params.get('only_edit') === '1';
  const mantencionAnterior = params.get('mantencion_anterior');

  const onBitacoraAdded = () => {
    callObservaciones(mantencion?.id_cliente);
  };

  const handleDownloadMantencionAnterior = () => {
    if (mantencionAnterior) {
      openDowloadMantencion({ id_mantencion: mantencionAnterior });
    }
  };

  // Tab state: checklist | estados | bitacora
  const [tab, setTab] = useState('checklist');

  // Contador de problemas (actividades RECHAZADO), considerando overrides del
  // provider para que el badge refleje el estado de la sesión actual.
  const problemCount = useMemo(() => {
    const flat = (Ascensores || []).flatMap((a) => a?.ActividadRespuestas || []);
    return flat.reduce((n, a) => {
      const eff = estados?.[a.id] !== undefined ? estados[a.id] : a.estado;
      return n + (eff === 'RECHAZADO' ? 1 : 0);
    }, 0);
  }, [Ascensores, estados]);

  // Guardar con toast de confirmación. El error (si falla) lo muestra save().
  const handleSave = async () => {
    const ok = await save(id);
    if (ok) message.success('Mantención guardada');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 180px)' }}>
      <MantencionHeader
        nro={mantencion?.id}
        edificio={mantencion?.Cliente?.name || 'Cargando...'}
        saveStatus={saveStatus}
        onSave={handleSave}
        onFinish={() => openFinish(id)}
        showFinish={!onlyEdit}
        isOnline={isOnline}
        hasUnsynced={hasUnsyncedChanges}
        extraAction={
          <>
            {mantencionAnterior && (
              <Button icon={<DownloadOutlined />} onClick={handleDownloadMantencionAnterior}>
                Anterior
              </Button>
            )}
            {!onlyEdit &&
              (mantencion?.inicio_visita_at ? (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}
                >
                  <PlayCircleOutlined style={{ marginRight: 4 }} />
                  {'En sitio · desde '}
                  {dayjs(mantencion.inicio_visita_at).format('HH:mm')}
                </Typography.Text>
              ) : (
                <Button icon={<PlayCircleOutlined />} loading={iniciando} onClick={onIniciarVisita}>
                  Iniciar visita
                </Button>
              ))}
          </>
        }
      />

      {isDesktop && <MantencionTopTabs tab={tab} onChange={setTab} problems={problemCount} />}

      <div style={{ flex: 1, paddingBottom: 16 }}>
        {tab === 'checklist' && (
          <MantencionTabShell width="checklist">
            <ChecklistTab
              Ascensores={Ascensores}
              activeAscensorId={activeAscensorId}
              setActiveAscensorId={setActiveAscensorId}
              ascensorStats={ascensorStats}
            />
          </MantencionTabShell>
        )}
        {tab === 'estados' && !isLoading && (
          <MantencionTabShell width="narrow">
            <AscensoresMaintanceToDoForm ascensores={Ascensores} />
            <GeneralMaintanceToDoForm observaciones={mantencion?.observaciones} />
          </MantencionTabShell>
        )}
        {tab === 'bitacora' && (
          <MantencionTabShell width="narrow">
            <BitacoraEdificio
              idCliente={mantencion?.id_cliente}
              observaciones={observaciones}
              onAdded={onBitacoraAdded}
            />
          </MantencionTabShell>
        )}
        {tab === 'componentes' && (
          <MantencionTabShell width="wide">
            {activeAscensorId ? (
              <ComponentesEquipo idEquipo={activeAscensorId} />
            ) : (
              <Empty description="Seleccioná un ascensor para ver sus componentes" />
            )}
          </MantencionTabShell>
        )}
      </div>

      {!isDesktop && <MantencionBottomNav tab={tab} onChange={setTab} problems={problemCount} />}

      <Modal open={finishOpen} onCancel={() => setFinishOpen(false)} footer={null}>
        <FinishMaintanceForm
          idMantencion={mantencion?.id}
          handleModal={() => setFinishOpen(!finishOpen)}
        />
      </Modal>

      <RevisarRedaccionDrawer
        open={revisionOpen}
        loading={revisionLoading}
        items={revisionItems}
        onConfirm={handleRevisionConfirm}
        onSkip={handleRevisionSkip}
        onRegenerate={handleRegenerate}
      />

      <Modal {...modalProps} footer={false} onCancel={() => setModalProps(defaultModalProps)} />
    </div>
  );
};

export default Mantencion;
